import type { SupportedLocale } from "../../lib/localeRoutes";

export interface Allergen {
  id: string;
  names: Record<SupportedLocale, string>;
}

const rows = [
  ["peanut", "Peanuts", "Yer fıstığı", "Erdnüsse", "Cacahuetes", "Арахис", "Αράπικα φιστίκια"],
  ["sesame", "Sesame", "Susam", "Sesam", "Sésamo", "Кунжут", "Σουσάμι"],
  ["milk", "Milk", "Süt", "Milch", "Leche", "Молоко", "Γάλα"],
  ["egg", "Eggs", "Yumurta", "Eier", "Huevos", "Яйца", "Αυγά"],
  ["wheat", "Wheat", "Buğday", "Weizen", "Trigo", "Пшеница", "Σιτάρι"],
  ["soy", "Soy", "Soya", "Soja", "Soja", "Соя", "Σόγια"],
  ["fish", "Fish", "Balık", "Fisch", "Pescado", "Рыба", "Ψάρια"],
  ["shrimp", "Prawns & shrimp", "Karides", "Garnelen", "Gambas y camarones", "Креветки", "Γαρίδες"],
  ["hazelnut", "Hazelnuts", "Fındık", "Haselnüsse", "Avellanas", "Фундук", "Φουντούκια"],
  ["pistachio", "Pistachios", "Antep fıstığı", "Pistazien", "Pistachos", "Фисташки", "Φιστίκια Αιγίνης"],
  ["walnut", "Walnuts", "Ceviz", "Walnüsse", "Nueces", "Грецкие орехи", "Καρύδια"],
  ["almond", "Almonds", "Badem", "Mandeln", "Almendras", "Миндаль", "Αμύγδαλα"],
  ["cashew", "Cashews", "Kaju", "Cashewkerne", "Anacardos", "Кешью", "Κάσιους"],
  ["mustard", "Mustard", "Hardal", "Senf", "Mostaza", "Горчица", "Μουστάρδα"],
  ["celery", "Celery", "Kereviz", "Sellerie", "Apio", "Сельдерей", "Σέλινο"],
  ["lupin", "Lupin", "Acı bakla", "Lupinen", "Altramuces", "Люпин", "Λούπινο"],
  ["kiwi", "Kiwi", "Kivi", "Kiwi", "Kiwi", "Киви", "Ακτινίδιο"],
  ["strawberry", "Strawberry", "Çilek", "Erdbeere", "Fresa", "Клубника", "Φράουλα"],
  ["banana", "Banana", "Muz", "Banane", "Plátano", "Банан", "Μπανάνα"],
  ["avocado", "Avocado", "Avokado", "Avocado", "Aguacate", "Авокадо", "Αβοκάντο"],
  ["tomato", "Tomato", "Domates", "Tomate", "Tomate", "Помидор", "Ντομάτα"],
  ["peach", "Peach", "Şeftali", "Pfirsich", "Melocotón", "Персик", "Ροδάκινο"],
  ["garlic", "Garlic", "Sarımsak", "Knoblauch", "Ajo", "Чеснок", "Σκόρδο"],
  ["coconut", "Coconut", "Hindistan cevizi", "Kokosnuss", "Coco", "Кокос", "Καρύδα"],
  ["lentil", "Lentils", "Mercimek", "Linsen", "Lentejas", "Чечевица", "Φακές"],
  ["chickpea", "Chickpeas", "Nohut", "Kichererbsen", "Garbanzos", "Нут", "Ρεβίθια"],
  ["oat", "Oats", "Yulaf", "Hafer", "Avena", "Овёс", "Βρώμη"],
] as const;

export const ALLERGENS: readonly Allergen[] = rows.map(([id, en, tr, de, es, ru, el]) => ({
  id, names: { en, tr, de, es, ru, el },
}));
export const LANGUAGE_NAMES: Record<SupportedLocale, string> = {
  el: "Ελληνικά", en: "English", tr: "Türkçe", de: "Deutsch", es: "Español", ru: "Русский",
};
export const CARD_LANGUAGES: readonly SupportedLocale[] = ["en", "tr", "de", "es", "ru", "el"];

const aliases: Partial<Record<SupportedLocale, Record<string, string>>> = {
  el: { αραχιδες: "peanut", "φυστικια αιγινης": "pistachio", ακτινιδια: "kiwi", φραουλες: "strawberry", μπανανες: "banana", ντοματες: "tomato", ροδακινα: "peach", καρυδες: "coconut", αυγο: "egg", αβγο: "egg", αβγα: "egg", καρυδι: "walnut", αμυγδαλο: "almond", φουντουκι: "hazelnut", ρεβιθι: "chickpea" },
  en: { strawberry: "strawberry", strawberries: "strawberry", kiwis: "kiwi", kiwifruit: "kiwi", bananas: "banana", avocados: "avocado", tomatoes: "tomato", peaches: "peach", coconuts: "coconut", shrimp: "shrimp", prawns: "shrimp", peanut: "peanut", egg: "egg", walnut: "walnut", almond: "almond", cashew: "cashew", hazelnut: "hazelnut", pistachio: "pistachio", chickpea: "chickpea", lentil: "lentil", oat: "oat" },
  de: { erdbeeren: "strawberry", kiwis: "kiwi", bananen: "banana", tomaten: "tomato", pfirsiche: "peach" },
  es: { fresas: "strawberry", kiwis: "kiwi", aguacates: "avocado", tomates: "tomato" },
  ru: { овес: "oat", бананы: "banana", помидоры: "tomato", персики: "peach" },
};

function normalize(text: string, locale: SupportedLocale): string {
  return text.trim().toLocaleLowerCase(locale).normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ");
}

export function findAllergen(text: string, locale: SupportedLocale): Allergen | undefined {
  const value = normalize(text, locale);
  const alias = aliases[locale]?.[value];
  return ALLERGENS.find(item => item.id === alias || normalize(item.names[locale], locale) === value);
}

export interface CardCopy { title: string; preparation: string; unsure: string; doctor: string }
export const CARD_COPY: Record<SupportedLocale, CardCopy> = {
  el: { title: "Έχω αλλεργία στα εξής:", preparation: "Ελέγξτε τα συστατικά και αν το φαγητό έρχεται σε επαφή με αυτά τα τρόφιμα κατά την προετοιμασία.", unsure: "Αν δεν είστε σίγουροι, ενημερώστε με.", doctor: "Ζητήστε γιατρό στον χώρο σας" },
  en: { title: "I am allergic to:", preparation: "Check ingredients and contact with these foods during preparation.", unsure: "If unsure, tell me.", doctor: "Request a doctor to your location" },
  tr: { title: "Şunlara alerjim var:", preparation: "Malzemeleri ve hazırlama sırasında bu gıdalarla teması kontrol edin.", unsure: "Emin değilseniz bana söyleyin.", doctor: "Bulunduğunuz yere doktor çağırın" },
  de: { title: "Ich bin allergisch gegen:", preparation: "Prüfen Sie die Zutaten und den Kontakt mit diesen Lebensmitteln bei der Zubereitung.", unsure: "Bei Unsicherheit sagen Sie mir bitte Bescheid.", doctor: "Fordern Sie einen Arzt an Ihren Standort an" },
  es: { title: "Tengo alergia a:", preparation: "Compruebe los ingredientes y el contacto con estos alimentos durante la preparación.", unsure: "Si tiene dudas, dígamelo.", doctor: "Solicite un médico en su ubicación" },
  ru: { title: "У меня аллергия на:", preparation: "Проверьте состав и контакт с этими продуктами при приготовлении.", unsure: "Если вы не уверены, скажите мне.", doctor: "Вызовите врача к себе" },
};
